import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Pencil, Plus, Trash2, UploadCloud } from 'lucide-react';
import { visionRoutingAPI } from '../../services/api';
import { toast } from '../../components/ui/use-toast';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

type Signal = {
  id: number;
  key: string;
  description: string;
  examples?: string[] | null;
  confidence_threshold: number;
  enabled: boolean;
};

type ImageType = {
  key: string;
  description: string;
  live: boolean;
  enabled_for_account: boolean;
  registry_version: number | null;
  model_provider?: string | null;
  model_endpoint_url?: string | null;
  prompt_template?: string | null;
  // 2026-09-10 (user: "can u pull use good mdels for these"): local/
  // self-hosted model file (model_type says which of yolo/paligemma) and
  // Hugging Face repo-id-referenced classifier, alongside the existing
  // hosted/custom-endpoint modes above.
  model_type?: string | null;
  model_file_path?: string | null;
  model_repo_id?: string | null;
  // 2026-09-11 (user: "you should keep calm and wash face... visit
  // facility u see x xx signs"): fixed text shown after the model's own
  // observations -- optional, per-category (only makes sense for a
  // condition like skin_condition, not chest_xray/lab_report).
  self_care_advice?: string | null;
  escalation_signs?: string | null;
  // 2026-09-11 (user: "where is the detail?" -- a PDF lab report only got
  // a generic thank-you; "can we make it like in signals itself we can
  // configure"). Opt-in per category, default false -- a PDF/Word reply
  // just isn't vision-routable unless this is explicitly turned on.
  analyze_document_text?: boolean;
  // 2026-09-14 (user: "can we creating this vision signal from
  // frontend"): true for an account-created category, false for one of
  // the 3 original built-ins -- only a custom, never-configured category
  // can be deleted from this page.
  is_custom?: boolean;
};

// 2026-09-10: a short, honest list -- only entries with a confirmed real
// Hugging Face repo id (checked against transformers'
// AutoModelForImageClassification/AutoImageProcessor compatibility --
// several well-known chest/skin models on Hugging Face need the separate
// torchxrayvision package or trust_remote_code and would silently fail
// here, so this list is deliberately short rather than exhaustive).
// 2026-09-14 (user: "can u simplify why can't i have these or current
// switchble, like i should be able to navigate or pick from frontend
// itself"): added skin_condition's two real candidates the same way.
// `caution`, when present, renders as an extra warning line under stats
// -- for a classifier (as opposed to the hosted default), picking it
// REPLACES the current prompt-driven text description with a flat
// label/confidence list, so a category with an already-tuned hosted
// prompt (skin_condition) needs that tradeoff spelled out up front,
// not discovered after saving.
const KNOWN_HF_MODELS: Record<string, { label: string; repoId: string; stats: string; caution?: string }[]> = {
  chest_xray: [
    {
      label: 'MouGam/nih-chestxray14-densenet121',
      repoId: 'MouGam/nih-chestxray14-densenet121',
      stats: 'DenseNet121 · 14 chest findings · multi-label (more than one can apply at once)',
    },
  ],
  skin_condition: [
    {
      label: 'anismizi/skin-type-classifier',
      repoId: 'anismizi/skin-type-classifier',
      stats: 'ResNet50 (~25M params) · oily vs. dry skin only · light & fast on CPU',
      caution: 'Replaces the current description with just "oily" or "dry" -- doesn\'t identify a condition at all. Consider a separate new category for this instead of switching skin_condition itself.',
    },
    {
      label: 'Anwarkh1/Skin_Cancer-Image_Classification',
      repoId: 'Anwarkh1/Skin_Cancer-Image_Classification',
      stats: 'ViT-Base (~86M params) · 7 lesion types · heavier, noticeably slower on CPU than the option above',
      caution: 'Trained on close-up clinical dermoscopy images, not ordinary phone photos -- accuracy on a real WhatsApp photo is unverified. Also replaces today\'s tuned text description with a flat label/confidence list.',
    },
  ],
};

/**
 * 2026-09-10, simplified (live feedback: "way too confusing"). Dropped
 * tabs (both sections visible at once — one less click, easier to see
 * this is one page, not two), dropped every badge/version/lock-icon that
 * only meant something to an engineer, and moved a signal's
 * examples/threshold out of the always-visible row into the edit dialog
 * only. An image type with no toggle shown at all ("Coming soon" text,
 * no switch) reads as "not ready" instead of "broken" — the earlier
 * disabled-switch version was exactly the "why is this blocked" question
 * that prompted this rewrite.
 */
const SignalsAndImageRoutingPage: React.FC = () => {
  const navigate = useNavigate();

  const [signals, setSignals] = useState<Signal[]>([]);
  const [imageTypes, setImageTypes] = useState<ImageType[]>([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSignal, setEditingSignal] = useState<Signal | null>(null);
  const [formKey, setFormKey] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formExamples, setFormExamples] = useState('');
  const [formThreshold, setFormThreshold] = useState(70);
  const [saving, setSaving] = useState(false);

  // 2026-09-14 (user: "can we creating this vision signal from frontend,
  // is it not possible?"): a separate, smaller dialog just for naming a
  // brand-new category -- Configure (below) then handles picking its
  // model/prompt, same two-step flow the signals section doesn't need
  // (a signal has no separate "model" to configure).
  const [newCategoryDialogOpen, setNewCategoryDialogOpen] = useState(false);
  const [newCategoryKey, setNewCategoryKey] = useState('');
  const [newCategoryDescription, setNewCategoryDescription] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);

  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [editingImageType, setEditingImageType] = useState<ImageType | null>(null);
  const [modelKind, setModelKind] = useState<'hosted' | 'custom' | 'huggingface' | 'local'>('hosted');
  const [modelDescription, setModelDescription] = useState('');
  const [modelPromptTemplate, setModelPromptTemplate] = useState('');
  const [modelEndpointUrl, setModelEndpointUrl] = useState('');
  const [modelApiKey, setModelApiKey] = useState('');
  const [modelRepoId, setModelRepoId] = useState('');
  const [modelType, setModelType] = useState<'yolo' | 'paligemma'>('yolo');
  const [modelFilePath, setModelFilePath] = useState('');
  const [modelSelfCareAdvice, setModelSelfCareAdvice] = useState('');
  const [modelEscalationSigns, setModelEscalationSigns] = useState('');
  const [modelAnalyzeDocumentText, setModelAnalyzeDocumentText] = useState(false);
  const [uploadingModel, setUploadingModel] = useState(false);
  const [savingModel, setSavingModel] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // 2026-09-10: a Hugging Face classifier and a YOLO detector don't take a
  // text prompt at all -- run_huggingface_inference/run_yolo_inference
  // (local_model_inference.py) neither accepts one. Only hosted, custom,
  // and local+PaliGemma actually use it. Derived here so both the save
  // validation and the Prompt field's own rendering agree.
  const promptApplies = modelKind === 'hosted' || modelKind === 'custom' || (modelKind === 'local' && modelType === 'paligemma');

  const load = async () => {
    setLoading(true);
    try {
      const [signalsData, imageTypesData] = await Promise.all([
        visionRoutingAPI.getSignals(),
        visionRoutingAPI.getImageTypes(),
      ]);
      setSignals(signalsData);
      setImageTypes(imageTypesData);
    } catch (error: any) {
      toast({
        title: 'Could not load this page',
        description: error?.response?.data?.detail,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openNewSignal = () => {
    setEditingSignal(null);
    setFormKey('');
    setFormDescription('');
    setFormExamples('');
    setFormThreshold(70);
    setDialogOpen(true);
  };

  const openEditSignal = (signal: Signal) => {
    setEditingSignal(signal);
    setFormKey(signal.key);
    setFormDescription(signal.description);
    setFormExamples((signal.examples || []).join('\n'));
    setFormThreshold(Math.round(signal.confidence_threshold * 100));
    setDialogOpen(true);
  };

  const saveSignal = async () => {
    if (!formKey.trim() || !formDescription.trim()) {
      toast({ title: 'A name and description are both required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    const examples = formExamples
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    try {
      if (editingSignal) {
        await visionRoutingAPI.updateSignal(editingSignal.id, {
          description: formDescription.trim(),
          examples,
          confidence_threshold: formThreshold / 100,
        });
        toast({ title: 'Signal updated' });
      } else {
        await visionRoutingAPI.createSignal({
          key: formKey.trim(),
          description: formDescription.trim(),
          examples,
          confidence_threshold: formThreshold / 100,
          enabled: true,
        });
        toast({ title: 'Signal created' });
      }
      setDialogOpen(false);
      await load();
    } catch (error: any) {
      toast({
        title: 'Could not save',
        description: error?.response?.data?.detail,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleSignalEnabled = async (signal: Signal, enabled: boolean) => {
    setSignals((prev) => prev.map((s) => (s.id === signal.id ? { ...s, enabled } : s)));
    try {
      await visionRoutingAPI.updateSignal(signal.id, { enabled });
    } catch {
      setSignals((prev) => prev.map((s) => (s.id === signal.id ? { ...s, enabled: !enabled } : s)));
      toast({ title: 'Could not update', variant: 'destructive' });
    }
  };

  const deleteSignal = async (signal: Signal) => {
    if (!window.confirm(`Delete "${signal.key}"? Any rule using it will stop matching.`)) return;
    try {
      await visionRoutingAPI.deleteSignal(signal.id);
      toast({ title: 'Deleted' });
      await load();
    } catch {
      toast({ title: 'Could not delete', variant: 'destructive' });
    }
  };

  const toggleImageType = async (imageType: ImageType, enabled: boolean) => {
    setImageTypes((prev) =>
      prev.map((it) => (it.key === imageType.key ? { ...it, enabled_for_account: enabled } : it)),
    );
    try {
      if (enabled) await visionRoutingAPI.enableImageType(imageType.key);
      else await visionRoutingAPI.disableImageType(imageType.key);
    } catch (error: any) {
      setImageTypes((prev) =>
        prev.map((it) => (it.key === imageType.key ? { ...it, enabled_for_account: !enabled } : it)),
      );
      toast({
        title: 'Could not update',
        description: error?.response?.data?.detail,
        variant: 'destructive',
      });
    }
  };

  const openNewImageType = () => {
    setNewCategoryKey('');
    setNewCategoryDescription('');
    setNewCategoryDialogOpen(true);
  };

  const createImageType = async () => {
    if (!newCategoryKey.trim() || !newCategoryDescription.trim()) {
      toast({ title: 'A name and description are both required', variant: 'destructive' });
      return;
    }
    setCreatingCategory(true);
    try {
      const created = await visionRoutingAPI.createImageType({
        key: newCategoryKey.trim(),
        description: newCategoryDescription.trim(),
      });
      toast({ title: 'Category created — now add a model and prompt below' });
      setNewCategoryDialogOpen(false);
      await load();
      // Jump straight into Configure for it, same as clicking the pencil
      // icon on the row that just appeared -- one less click.
      openConfigureImageType(created);
    } catch (error: any) {
      toast({
        title: 'Could not create',
        description: error?.response?.data?.detail,
        variant: 'destructive',
      });
    } finally {
      setCreatingCategory(false);
    }
  };

  const deleteImageType = async (imageType: ImageType) => {
    if (!window.confirm(`Delete "${imageType.key}"? This can't be undone.`)) return;
    try {
      await visionRoutingAPI.deleteImageType(imageType.key);
      toast({ title: 'Deleted' });
      await load();
    } catch (error: any) {
      toast({
        title: 'Could not delete',
        description: error?.response?.data?.detail,
        variant: 'destructive',
      });
    }
  };

  const openConfigureImageType = (imageType: ImageType) => {
    setEditingImageType(imageType);
    const provider = imageType.model_provider;
    setModelKind(
      provider === 'custom' ? 'custom' : provider === 'huggingface' ? 'huggingface' : provider === 'local' ? 'local' : 'hosted',
    );
    setModelDescription(imageType.description);
    setModelPromptTemplate(imageType.prompt_template || '');
    setModelEndpointUrl(imageType.model_endpoint_url || '');
    setModelApiKey(''); // never pre-filled -- write-only secret, blank means "keep the existing one" server-side isn't implemented yet, so re-enter it to change the model
    setModelRepoId(imageType.model_repo_id || '');
    setModelType((imageType.model_type as 'yolo' | 'paligemma') || 'yolo');
    setModelFilePath(imageType.model_file_path || '');
    setModelSelfCareAdvice(imageType.self_care_advice || '');
    setModelEscalationSigns(imageType.escalation_signs || '');
    setModelAnalyzeDocumentText(!!imageType.analyze_document_text);
    setModelDialogOpen(true);
  };

  const uploadModelFile = async (file: File) => {
    if (!editingImageType) return;
    setUploadingModel(true);
    try {
      const result = await visionRoutingAPI.uploadImageTypeModel(editingImageType.key, file);
      setModelFilePath(result.model_file_path);
      toast({ title: `Uploaded ${file.name}` });
    } catch (error: any) {
      toast({
        title: 'Upload failed',
        description: error?.response?.data?.detail,
        variant: 'destructive',
      });
    } finally {
      setUploadingModel(false);
    }
  };

  const saveImageTypeConfig = async () => {
    if (!editingImageType) return;
    if (!modelDescription.trim() || (promptApplies && !modelPromptTemplate.trim())) {
      toast({
        title: promptApplies ? 'A description and a prompt are both required' : 'A description is required',
        variant: 'destructive',
      });
      return;
    }
    if (modelKind === 'custom' && !modelEndpointUrl.trim()) {
      toast({ title: 'Your model needs an endpoint URL', variant: 'destructive' });
      return;
    }
    if (modelKind === 'huggingface' && !modelRepoId.trim()) {
      toast({ title: 'Enter a Hugging Face repo id, e.g. "org/model-name"', variant: 'destructive' });
      return;
    }
    if (modelKind === 'local' && !modelFilePath.trim()) {
      toast({ title: 'Upload a model file first', variant: 'destructive' });
      return;
    }
    setSavingModel(true);
    try {
      await visionRoutingAPI.configureImageType(editingImageType.key, {
        description: modelDescription.trim(),
        prompt_template: modelPromptTemplate.trim(),
        model_provider:
          modelKind === 'custom' ? 'custom' : modelKind === 'huggingface' ? 'huggingface' : modelKind === 'local' ? 'local' : 'groq_qwen_vision',
        model_endpoint_url: modelKind === 'custom' ? modelEndpointUrl.trim() : '',
        model_api_key: modelKind === 'custom' ? modelApiKey.trim() : '',
        model_repo_id: modelKind === 'huggingface' ? modelRepoId.trim() : '',
        model_type: modelKind === 'local' ? modelType : '',
        model_file_path: modelKind === 'local' ? modelFilePath : '',
        self_care_advice: modelSelfCareAdvice.trim(),
        escalation_signs: modelEscalationSigns.trim(),
        analyze_document_text: modelAnalyzeDocumentText,
      });
      toast({ title: 'Saved — this image type is now live' });
      setModelDialogOpen(false);
      await load();
    } catch (error: any) {
      toast({
        title: 'Could not save',
        description: error?.response?.data?.detail,
        variant: 'destructive',
      });
    } finally {
      setSavingModel(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-8 max-w-2xl">
      <div>
        <Button variant="ghost" size="sm" className="-ml-2 mb-2" onClick={() => navigate('/dashboard/automation')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Automation Rules
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">Signals &amp; Image Routing</h1>
      </div>

      {/* ---------------- Signals ---------------- */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold">Signals</h2>
            <p className="text-sm text-muted-foreground">
              Things the AI should notice in a conversation, so a rule can react to them.
            </p>
          </div>
          <Button size="sm" onClick={openNewSignal}>
            <Plus className="h-4 w-4 mr-1" /> New
          </Button>
        </div>

        {signals.length === 0 && (
          <p className="text-sm text-muted-foreground py-4">
            None yet. For example, "customer mentions a rash" — a rule could ask for a photo
            automatically.
          </p>
        )}

        <div className="rounded-md border divide-y">
          {signals.map((signal) => (
            <div key={signal.id} className="flex items-center gap-3 px-3 py-2.5">
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium truncate ${signal.enabled ? '' : 'text-muted-foreground'}`}>
                  {signal.key}
                </p>
                <p className="text-xs text-muted-foreground truncate">{signal.description}</p>
              </div>
              <Switch
                checked={signal.enabled}
                onCheckedChange={(checked) => toggleSignalEnabled(signal, checked)}
              />
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEditSignal(signal)}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteSignal(signal)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      </section>

      {/* ---------------- Image types ---------------- */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold">Image Types</h2>
            <p className="text-sm text-muted-foreground">
              When a photo arrives, this is what reads it and describes what's in it — e.g. a skin
              photo. Add your own category below, or configure one of the built-in ones.
            </p>
          </div>
          <Button size="sm" onClick={openNewImageType}>
            <Plus className="h-4 w-4 mr-1" /> New
          </Button>
        </div>

        <div className="rounded-md border divide-y">
          {imageTypes.map((imageType) => (
            <div key={imageType.key} className="flex items-center gap-3 px-3 py-2.5">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{imageType.key}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {imageType.live
                    ? imageType.description
                    : 'Not set up yet — add a model and a prompt to turn this on.'}
                </p>
              </div>
              {imageType.live && (
                <Switch
                  checked={imageType.enabled_for_account}
                  onCheckedChange={(checked) => toggleImageType(imageType, checked)}
                />
              )}
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openConfigureImageType(imageType)}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              {imageType.is_custom && !imageType.live && (
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteImageType(imageType)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </section>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSignal ? 'Edit signal' : 'New signal'}</DialogTitle>
            <DialogDescription>
              Describe the situation in plain language — this steers the AI, it isn't shown to
              customers.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={formKey}
                onChange={(e) => setFormKey(e.target.value)}
                placeholder="e.g. skin_image_needed"
                disabled={!!editingSignal}
              />
            </div>
            <div>
              <Label>What should the AI watch for?</Label>
              <Textarea
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                placeholder="A visible skin condition described in the conversation — a rash, lesion, discoloration, or similar."
                rows={3}
              />
            </div>
            <div>
              <Label>Example phrases (optional, one per line)</Label>
              <Textarea
                value={formExamples}
                onChange={(e) => setFormExamples(e.target.value)}
                placeholder={'I have a rash on my arm\nthere\'s a weird spot on my leg'}
                rows={2}
              />
            </div>
            <div>
              <Label>Sensitivity ({formThreshold}%)</Label>
              <input
                type="range"
                min={50}
                max={95}
                value={formThreshold}
                onChange={(e) => setFormThreshold(Number(e.target.value))}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground mt-1">Higher = stricter, fewer false matches.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveSignal} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={newCategoryDialogOpen} onOpenChange={setNewCategoryDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New image type</DialogTitle>
            <DialogDescription>
              Name it and describe what it's for — you'll pick the model and prompt next.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={newCategoryKey}
                onChange={(e) => setNewCategoryKey(e.target.value)}
                placeholder="e.g. wound_photo"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Lowercase letters, numbers, and underscores only (e.g. "wound_photo").
              </p>
            </div>
            <div>
              <Label>What kind of photo is this?</Label>
              <Textarea
                value={newCategoryDescription}
                onChange={(e) => setNewCategoryDescription(e.target.value)}
                placeholder="A photo of an open wound or injury."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewCategoryDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={createImageType} disabled={creatingCategory}>
              {creatingCategory && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={modelDialogOpen} onOpenChange={setModelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Configure {editingImageType?.key}</DialogTitle>
            <DialogDescription>
              Saving this makes it live immediately — you can change it again any time.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Description (shown on this page)</Label>
              <Input value={modelDescription} onChange={(e) => setModelDescription(e.target.value)} />
            </div>

            <div>
              <Label>Model</Label>
              <div className="flex flex-wrap rounded-md border overflow-hidden mt-1">
                <button
                  type="button"
                  className={`flex-1 min-w-[45%] text-sm py-1.5 ${modelKind === 'hosted' ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
                  onClick={() => setModelKind('hosted')}
                >
                  Our default model
                </button>
                <button
                  type="button"
                  className={`flex-1 min-w-[45%] text-sm py-1.5 border-l ${modelKind === 'huggingface' ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
                  onClick={() => setModelKind('huggingface')}
                >
                  Hugging Face model
                </button>
                <button
                  type="button"
                  className={`flex-1 min-w-[45%] text-sm py-1.5 border-l border-t ${modelKind === 'local' ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
                  onClick={() => setModelKind('local')}
                >
                  Upload a model file
                </button>
                <button
                  type="button"
                  className={`flex-1 min-w-[45%] text-sm py-1.5 border-l border-t ${modelKind === 'custom' ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
                  onClick={() => setModelKind('custom')}
                >
                  My own server
                </button>
              </div>
              {modelKind === 'hosted' && (
                <p className="text-xs text-muted-foreground mt-1">
                  Uses our built-in vision model — nothing else to set up.
                </p>
              )}
              {modelKind === 'custom' && (
                <p className="text-xs text-muted-foreground mt-1">
                  Calls a model you host yourself. It needs one HTTP endpoint that accepts{' '}
                  <code className="text-xs bg-muted px-1 rounded">{'{ image_base64, mime_type, prompt }'}</code>{' '}
                  and returns{' '}
                  <code className="text-xs bg-muted px-1 rounded">{'{ "content": "..." }'}</code>.
                </p>
              )}
              {modelKind === 'huggingface' && (
                <p className="text-xs text-muted-foreground mt-1">
                  Downloads a published classifier by its Hugging Face repo id, the first time it's
                  used — nothing to upload.
                </p>
              )}
              {modelKind === 'local' && (
                <p className="text-xs text-muted-foreground mt-1">
                  Upload your own YOLO or PaliGemma weights file — it runs on our server, in-process,
                  never sent anywhere else.
                </p>
              )}
            </div>

            {modelKind === 'custom' && (
              <>
                <div>
                  <Label>Your model's endpoint URL</Label>
                  <Input
                    value={modelEndpointUrl}
                    onChange={(e) => setModelEndpointUrl(e.target.value)}
                    placeholder="https://your-server.example.com/describe"
                  />
                </div>
                <div>
                  <Label>API key (optional, sent as a Bearer token)</Label>
                  <Input
                    type="password"
                    value={modelApiKey}
                    onChange={(e) => setModelApiKey(e.target.value)}
                    placeholder={editingImageType?.model_provider === 'custom' ? 'Leave blank to keep the current key' : ''}
                  />
                </div>
              </>
            )}

            {modelKind === 'huggingface' && (
              <div>
                <Label>Hugging Face repo id</Label>
                <Input
                  value={modelRepoId}
                  onChange={(e) => setModelRepoId(e.target.value)}
                  placeholder="org/model-name"
                />
                {editingImageType && (KNOWN_HF_MODELS[editingImageType.key] || []).length > 0 && (
                  <div className="mt-2 space-y-1.5">
                    <p className="text-xs text-muted-foreground">Or pick one we've checked:</p>
                    {(KNOWN_HF_MODELS[editingImageType.key] || []).map((m) => (
                      <button
                        key={m.repoId}
                        type="button"
                        onClick={() => setModelRepoId(m.repoId)}
                        className={`w-full text-left rounded-md border px-2.5 py-1.5 text-xs ${
                          modelRepoId === m.repoId ? 'border-primary bg-primary/5' : 'bg-background'
                        }`}
                      >
                        <span className="font-medium">{m.label}</span>
                        <span className="block text-muted-foreground">{m.stats}</span>
                        {m.caution && (
                          <span className="block text-amber-600 dark:text-amber-500 mt-0.5">⚠ {m.caution}</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {modelKind === 'local' && (
              <div>
                <Label>Model type</Label>
                <div className="flex rounded-md border overflow-hidden mt-1">
                  <button
                    type="button"
                    className={`flex-1 text-sm py-1.5 ${modelType === 'yolo' ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
                    onClick={() => setModelType('yolo')}
                  >
                    YOLO (object detection)
                  </button>
                  <button
                    type="button"
                    className={`flex-1 text-sm py-1.5 border-l ${modelType === 'paligemma' ? 'bg-primary text-primary-foreground' : 'bg-background'}`}
                    onClick={() => setModelType('paligemma')}
                  >
                    PaliGemma (vision-language)
                  </button>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingModel}
                  >
                    {uploadingModel ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    ) : (
                      <UploadCloud className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    {modelFilePath ? 'Replace file' : 'Choose file'}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pt,.onnx,.safetensors,.bin"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void uploadModelFile(file);
                      e.target.value = '';
                    }}
                  />
                  {modelFilePath && (
                    <span className="text-xs text-muted-foreground truncate">{modelFilePath.split(/[\\/]/).pop()}</span>
                  )}
                </div>
                {modelType === 'paligemma' && (
                  <p className="text-xs text-muted-foreground mt-1">
                    A multi-billion-parameter model on CPU-only hardware can take a real while per
                    photo — this fails open (falls back to normal handling) rather than hanging a
                    reply if it's too slow.
                  </p>
                )}
              </div>
            )}

            <div>
              <Label>Prompt</Label>
              <Textarea
                value={modelPromptTemplate}
                onChange={(e) => setModelPromptTemplate(e.target.value)}
                placeholder="Describe what is visible in this photo in plain language. Never name a diagnosis. Always recommend seeing a professional."
                rows={5}
              />
              <p className="text-xs text-muted-foreground mt-1">
                What the model is told to do with the photo. Every reply still passes through a
                fixed safety step afterward, regardless of what this prompt says.
              </p>
            </div>

            <div>
              <Label>Self-care advice (optional)</Label>
              <Textarea
                value={modelSelfCareAdvice}
                onChange={(e) => setModelSelfCareAdvice(e.target.value)}
                placeholder="e.g. In the meantime, keep the area clean, avoid touching or picking at it, and avoid harsh products until it settles."
                rows={2}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Fixed text — always the same every time, never written by the model. Shown after
                the observations. Leave blank to skip this (e.g. doesn't make sense for an X-ray
                or lab report).
              </p>
            </div>

            <div>
              <Label>When to see a doctor (optional)</Label>
              <Textarea
                value={modelEscalationSigns}
                onChange={(e) => setModelEscalationSigns(e.target.value)}
                placeholder="e.g. If you notice increasing pain, spreading redness or swelling, pus, or fever, please visit a health facility promptly."
                rows={2}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Fixed, concrete signs to watch for — also never written by the model. Shown right
                before the final "see a professional" line.
              </p>
            </div>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={modelAnalyzeDocumentText}
                onChange={(e) => setModelAnalyzeDocumentText(e.target.checked)}
              />
              <span>
                Also read PDF/Word documents (not just photos)
                <span className="block text-xs text-muted-foreground mt-0.5">
                  When someone replies with a PDF or Word file instead of a photo, extract its text
                  (OCR) and run the same prompt on that text instead. Off by default — a PDF/Word
                  reply is otherwise just captured with a plain "thanks, received" message.
                </span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModelDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveImageTypeConfig} disabled={savingModel}>
              {savingModel && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save &amp; turn on
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SignalsAndImageRoutingPage;
